import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NaturalLanguageFiller, type FillerFieldDef } from '@/components/NaturalLanguageFiller';
import { AccountCombobox } from '@/components/AccountCombobox';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import {
  COUNTRIES,
  DISQUALIFICATION_REASONS,
  SENIORITY_LEVELS,
  canonicaliseUrl,
  validateEmail,
  validatePhone,
  validateUrl,
} from '@/lib/validation';

/**
 * NewLeadModal — expanded form for lead capture that mirrors what
 * marketing/RevOps actually need on day one:
 *
 *  - Identity (first/last/email/phone/title/seniority)
 *  - Account link (existing combobox or pending company name buffer)
 *  - Source + attribution (UTM tracking, landing/referrer URLs) — the
 *    backend persists these on V163 once it lands; we send them
 *    forward-compatibly today so we never lose attribution data.
 *  - Consent (consent_source + auto-stamps consent_captured_at) — needed
 *    for GDPR/LGPD audit trail on inbound forms.
 *  - Disposition (lead_score, disqualification_reason, notes, tags).
 *
 * Validation is best-effort client-side; the server still owns the
 * canonical checks.
 */

const FILLER_SCHEMA: FillerFieldDef[] = [
  { name: 'firstName', label: 'First name', type: 'string' },
  { name: 'lastName', label: 'Last name', type: 'string' },
  { name: 'email', label: 'Email', type: 'string' },
  { name: 'phone', label: 'Phone', type: 'string' },
  { name: 'companyName', label: 'Company', type: 'string' },
  { name: 'title', label: 'Job title', type: 'string' },
  { name: 'country', label: 'Country (ISO-2)', type: 'string' },
];

const LANGUAGES = ['es', 'en', 'pt-BR'] as const;
const CONSENT_SOURCES = [
  'WEB_FORM', 'EMAIL_REPLY', 'VERBAL', 'IMPORT', 'EVENT', 'PARTNER', 'OTHER',
] as const;

interface CampaignOption {
  id: string;
  name: string;
}

interface CreatedLead {
  id: string;
  campaignId: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (lead: CreatedLead) => void;
  /**
   * When set, the campaign field is hidden and the new lead is created with
   * this id. Used by CampaignDetailPage where the campaign is unambiguous.
   */
  lockedCampaignId?: string;
  /**
   * When set, the lead is hard-linked to this account at creation time
   * (LEADS.ACCOUNT_ID is populated). The companyName field is hidden — the
   * backend backfills it from the account name. Used from AccountDetailPage.
   */
  lockedAccountId?: string;
  /**
   * Display label for the locked account, just for the read-only chip the
   * modal renders so the user knows what they're attaching to.
   */
  lockedAccountLabel?: string;
}

export function NewLeadModal({
  open,
  onClose,
  onCreated,
  lockedCampaignId,
  lockedAccountId,
  lockedAccountLabel,
}: Props) {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);

  // Identity
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [seniorityLevel, setSeniorityLevel] = useState('');
  const [country, setCountry] = useState('');
  const [languagePreference, setLanguagePreference] = useState('');

  // Account/campaign linking
  const [accountId, setAccountId] = useState<string | null>(null);
  // Held only as a transient buffer for the Natural-Language filler: when
  // the AI suggests a company name we keep it here so the AccountCombobox
  // can pre-populate its query and offer create-or-pick. Cleared once the
  // user resolves it (pick or create).
  const [pendingCompanyName, setPendingCompanyName] = useState('');
  const [campaignId, setCampaignId] = useState('');

  // Scoring / disposition
  const [leadScore, setLeadScore] = useState<string>('');
  const [disqualificationReason, setDisqualificationReason] = useState('');

  // Attribution (UTM + landing/referrer)
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [referrerUrl, setReferrerUrl] = useState('');

  // Consent (GDPR/LGPD audit trail)
  const [consentSource, setConsentSource] = useState('');
  const [consentCapturedAt, setConsentCapturedAt] = useState('');

  // Extras
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-stamp consent_captured_at whenever a consent source is chosen
  // (the rep can still override the date manually).
  useEffect(() => {
    if (consentSource && !consentCapturedAt) {
      setConsentCapturedAt(new Date().toISOString().slice(0, 10));
    }
  }, [consentSource, consentCapturedAt]);

  useEffect(() => {
    if (!open || lockedCampaignId) return;
    apiClient
      .get<CampaignOption[]>('/campaigns')
      .then(({ data }) => setCampaigns(data))
      .catch(() => setCampaigns([]));
  }, [open, lockedCampaignId]);

  // Default language preference from the current i18n session — a
  // sensible starting point until we read browser Accept-Language
  // on the public capture form.
  useEffect(() => {
    if (open && !languagePreference && i18n.language) {
      const lng = i18n.language.startsWith('pt') ? 'pt-BR'
        : i18n.language.startsWith('en') ? 'en' : 'es';
      setLanguagePreference(lng);
    }
  }, [open, languagePreference, i18n.language]);

  const emailError = useMemo(() => {
    const k = validateEmail(email);
    return k ? t(k) : null;
  }, [email, t]);
  const phoneError = useMemo(() => {
    const k = validatePhone(phone);
    return k ? t(k) : null;
  }, [phone, t]);
  const landingError = useMemo(() => {
    const k = validateUrl(landingPageUrl);
    return k ? t(k) : null;
  }, [landingPageUrl, t]);
  const referrerError = useMemo(() => {
    const k = validateUrl(referrerUrl);
    return k ? t(k) : null;
  }, [referrerUrl, t]);
  const scoreError = useMemo(() => {
    if (!leadScore) return null;
    const n = Number(leadScore);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      return t('validation.score.range');
    }
    return null;
  }, [leadScore, t]);

  if (!open) return null;

  function reset() {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setTitle('');
    setSeniorityLevel('');
    setCountry('');
    setLanguagePreference('');
    setAccountId(null);
    setPendingCompanyName('');
    setCampaignId('');
    setLeadScore('');
    setDisqualificationReason('');
    setUtmSource('');
    setUtmMedium('');
    setUtmCampaign('');
    setUtmTerm('');
    setUtmContent('');
    setLandingPageUrl('');
    setReferrerUrl('');
    setConsentSource('');
    setConsentCapturedAt('');
    setTags('');
    setNotes('');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (emailError || phoneError || landingError || referrerError || scoreError) {
      setError(t('validation.fix_errors'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { source: 'MANUAL' };
      if (firstName.trim()) body.firstName = firstName.trim();
      if (lastName.trim()) body.lastName = lastName.trim();
      if (email.trim()) body.email = email.trim().toLowerCase();
      if (phone.trim()) body.phone = phone.trim();
      if (title.trim()) body.title = title.trim();
      if (seniorityLevel) body.seniorityLevel = seniorityLevel;
      if (country) body.country = country;
      if (languagePreference) body.languagePreference = languagePreference;

      const effectiveAccountId = lockedAccountId ?? accountId ?? undefined;
      if (effectiveAccountId) body.accountId = effectiveAccountId;
      // If the AI filler dropped a company name and the rep submits without
      // resolving it through the combobox, send it as staging so the
      // backend's find-or-create resolves it server-side.
      if (!effectiveAccountId && pendingCompanyName.trim()) {
        body.companyName = pendingCompanyName.trim();
      }
      const effectiveCampaignId = lockedCampaignId ?? (campaignId || undefined);
      if (effectiveCampaignId) body.campaignId = effectiveCampaignId;

      if (leadScore && !Number.isNaN(Number(leadScore))) {
        body.leadScore = Number(leadScore);
      }
      if (disqualificationReason) body.disqualificationReason = disqualificationReason;

      if (utmSource.trim()) body.utmSource = utmSource.trim();
      if (utmMedium.trim()) body.utmMedium = utmMedium.trim();
      if (utmCampaign.trim()) body.utmCampaign = utmCampaign.trim();
      if (utmTerm.trim()) body.utmTerm = utmTerm.trim();
      if (utmContent.trim()) body.utmContent = utmContent.trim();
      if (landingPageUrl.trim()) body.landingPageUrl = canonicaliseUrl(landingPageUrl);
      if (referrerUrl.trim()) body.referrerUrl = canonicaliseUrl(referrerUrl);

      if (consentSource) body.consentSource = consentSource;
      if (consentCapturedAt) body.consentCapturedAt = consentCapturedAt;

      const tagArray = tags
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (tagArray.length > 0) body.tags = tagArray;
      if (notes.trim()) body.notes = notes.trim();

      const { data } = await apiClient.post<CreatedLead>('/leads', body);
      onCreated(data);
      reset();
      onClose();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('newLead.error_generic'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    reset();
    onClose();
  }

  const canSubmit =
    !submitting &&
    !emailError &&
    !phoneError &&
    !landingError &&
    !referrerError &&
    !scoreError &&
    (firstName.trim().length > 0 ||
      lastName.trim().length > 0 ||
      email.trim().length > 0 ||
      accountId !== null ||
      pendingCompanyName.trim().length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('newLead.title')}</h3>
          <NaturalLanguageFiller
            fieldSchema={FILLER_SCHEMA}
            onApply={(values) => {
              if (values.firstName) setFirstName(values.firstName);
              if (values.lastName) setLastName(values.lastName);
              if (values.email) setEmail(values.email);
              if (values.phone) setPhone(values.phone);
              if (values.title) setTitle(values.title);
              if (values.country) setCountry(String(values.country).toUpperCase());
              if (values.companyName && !lockedAccountId) {
                setPendingCompanyName(values.companyName);
              }
            }}
          />
        </div>
        <p className="mb-4 text-sm text-muted-foreground">{t('newLead.subtitle')}</p>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* ───── Identity ───── */}
          <FormSection title={t('newLead.section.identity')} defaultOpen>
            <FormGrid>
              <FormField label={t('newLead.first_name')}>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                />
              </FormField>
              <FormField label={t('newLead.last_name')}>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('newLead.email')} error={emailError}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>
              <FormField label={t('newLead.phone')} error={phoneError}>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('newLead.field.title')}>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </FormField>
              <FormField label={t('newLead.field.seniorityLevel')}>
                <select
                  value={seniorityLevel}
                  onChange={(e) => setSeniorityLevel(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.none')}</option>
                  {SENIORITY_LEVELS.map((s) => (
                    <option key={s} value={s}>
                      {t(`reference.seniority.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('newLead.field.country')}>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.none')}</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {t(c.nameKey)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('newLead.field.languagePreference')}>
                <select
                  value={languagePreference}
                  onChange={(e) => setLanguagePreference(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.none')}</option>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {t(`reference.language.${l}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* ───── Account & campaign link ───── */}
          <FormSection title={t('newLead.section.linking')} defaultOpen>
            {lockedAccountId ? (
              <FormField label={t('newLead.account')}>
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  {lockedAccountLabel ?? lockedAccountId}
                </div>
              </FormField>
            ) : (
              <FormField label={t('newLead.account')}>
                <AccountCombobox
                  value={accountId}
                  onChange={(id) => {
                    setAccountId(id);
                    if (id) setPendingCompanyName('');
                  }}
                  hint={pendingCompanyName || null}
                />
              </FormField>
            )}
            {!lockedCampaignId && (
              <FormField label={t('newLead.campaign')}>
                <select
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('newLead.no_campaign')}</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FormField>
            )}
          </FormSection>

          {/* ───── Attribution (UTM + landing / referrer) ───── */}
          <FormSection
            title={t('newLead.section.attribution')}
            subtitle={t('newLead.section.attributionHint')}
            defaultOpen={false}
          >
            <FormGrid>
              <FormField label={t('newLead.field.utmSource')} hint="google, linkedin, newsletter…">
                <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} />
              </FormField>
              <FormField label={t('newLead.field.utmMedium')} hint="cpc, organic, email…">
                <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormField label={t('newLead.field.utmCampaign')}>
              <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} />
            </FormField>
            <FormGrid>
              <FormField label={t('newLead.field.utmTerm')}>
                <Input value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} />
              </FormField>
              <FormField label={t('newLead.field.utmContent')}>
                <Input value={utmContent} onChange={(e) => setUtmContent(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormField label={t('newLead.field.landingPageUrl')} error={landingError}>
              <Input
                value={landingPageUrl}
                onChange={(e) => setLandingPageUrl(e.target.value)}
                placeholder="https://…"
              />
            </FormField>
            <FormField label={t('newLead.field.referrerUrl')} error={referrerError}>
              <Input
                value={referrerUrl}
                onChange={(e) => setReferrerUrl(e.target.value)}
                placeholder="https://…"
              />
            </FormField>
          </FormSection>

          {/* ───── Consent (GDPR/LGPD audit trail) ───── */}
          <FormSection
            title={t('newLead.section.consent')}
            subtitle={t('newLead.section.consentHint')}
            defaultOpen={false}
          >
            <FormGrid>
              <FormField label={t('newLead.field.consentSource')}>
                <select
                  value={consentSource}
                  onChange={(e) => setConsentSource(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.none')}</option>
                  {CONSENT_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`reference.consentSource.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('newLead.field.consentCapturedAt')}
                hint={t('newLead.hint.consentCapturedAt')}
              >
                <Input
                  type="date"
                  value={consentCapturedAt}
                  onChange={(e) => setConsentCapturedAt(e.target.value)}
                  disabled={!consentSource}
                />
              </FormField>
            </FormGrid>
          </FormSection>

          {/* ───── Scoring / disposition / more ───── */}
          <FormSection title={t('newLead.section.more')} defaultOpen={false}>
            <FormGrid>
              <FormField
                label={t('newLead.field.leadScore')}
                hint={t('newLead.hint.leadScore')}
                error={scoreError}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={leadScore}
                  onChange={(e) => setLeadScore(e.target.value)}
                />
              </FormField>
              <FormField label={t('newLead.field.disqualificationReason')}>
                <select
                  value={disqualificationReason}
                  onChange={(e) => setDisqualificationReason(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('common.none')}</option>
                  {DISQUALIFICATION_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`reference.disqualificationReason.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormField label={t('newLead.field.tags')} hint={t('newLead.hint.tags')}>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="webinar, q2-2026"
              />
            </FormField>
            <FormField label={t('newLead.field.notes')}>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </FormField>
          </FormSection>

          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {submitting ? t('newLead.creating') : t('newLead.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
